// ---------------------------------------------------------------------------
// Leaderboard configuration — game types, metrics, periods, thresholds
// ---------------------------------------------------------------------------

export const LEADERBOARD_HEADER_TITLE = 'Leaderboards';

export const GAME_TYPES = [
  { key: 'points', label: 'Points' },
  { key: 'unlimited', label: 'Unlimited' },
  { key: 'generations', label: 'Generations' },
  { key: 'riddled_in', label: 'Riddled-In' },
  { key: 'earth_impossible', label: 'Earth Impossible' },
  { key: 'earth_impossible_ish', label: 'Earth Impossible-ish' },
];

export const PERIODS = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'all_time', label: 'All-Time' },
];

// Special periods for the points leaderboard (no weekly/monthly — uses season)
export const POINTS_PERIODS = [
  { key: 'points_season', label: 'This Season' },
  { key: 'points_lifetime', label: 'All-Time' },
];

// Metrics available per game type.
// `scoreLabel` is the column header in the leaderboard table.
// `format` determines how to display the value.
// `sortHint` describes whether lower or higher is better (for display purposes).

export const METRICS_BY_GAME = {
  points: [
    { key: 'points_season', label: 'This Season', scoreLabel: 'Points', format: 'integer', sortHint: 'higher' },
    { key: 'points_lifetime', label: 'All-Time', scoreLabel: 'Points', format: 'integer', sortHint: 'higher' },
  ],
  unlimited: [
    { key: 'avg_time', label: 'Avg Solve Time', scoreLabel: 'Avg Time', format: 'time', sortHint: 'lower' },
    { key: 'avg_guesses', label: 'Avg Guesses', scoreLabel: 'Avg Guesses', format: 'decimal', sortHint: 'lower' },
    { key: 'win_rate', label: 'Win Rate', scoreLabel: 'Win %', format: 'percent', sortHint: 'higher' },
    { key: 'best_streak', label: 'Best Streak', scoreLabel: 'Streak', format: 'integer', sortHint: 'higher' },
  ],
  generations: [
    { key: 'avg_time', label: 'Avg Solve Time', scoreLabel: 'Avg Time', format: 'time', sortHint: 'lower' },
    { key: 'avg_guesses', label: 'Avg Guesses', scoreLabel: 'Avg Guesses', format: 'decimal', sortHint: 'lower' },
    { key: 'win_rate', label: 'Win Rate', scoreLabel: 'Win %', format: 'percent', sortHint: 'higher' },
  ],
  riddled_in: [
    { key: 'avg_time', label: 'Avg Solve Time', scoreLabel: 'Avg Time', format: 'time', sortHint: 'lower' },
    { key: 'avg_guesses', label: 'Avg Guesses', scoreLabel: 'Avg Guesses', format: 'decimal', sortHint: 'lower' },
    { key: 'solve_rate', label: 'Solve Rate', scoreLabel: 'Solve %', format: 'percent', sortHint: 'higher' },
  ],
  earth_impossible: [
    { key: 'avg_time', label: 'Avg Time', scoreLabel: 'Avg Time', format: 'time', sortHint: 'lower' },
    { key: 'avg_distance_km', label: 'Avg Distance', scoreLabel: 'Avg Distance', format: 'decimal', sortHint: 'lower' },
    { key: 'avg_final_guess_rate', label: 'Avg Final Guess Rate', scoreLabel: 'Avg Guess %', format: 'percent', sortHint: 'higher' },
  ],
  earth_impossible_ish: [
    { key: 'avg_time', label: 'Avg Time', scoreLabel: 'Avg Time', format: 'time', sortHint: 'lower' },
    { key: 'avg_distance_km', label: 'Avg Distance', scoreLabel: 'Avg Distance', format: 'decimal', sortHint: 'lower' },
    { key: 'avg_final_guess_rate', label: 'Avg Final Guess Rate', scoreLabel: 'Avg Guess %', format: 'percent', sortHint: 'higher' },
  ],
};

// Minimum rounds to qualify for any leaderboard for this game.
export const MIN_GAMES = {
  points: 0, // points are self-gating
  unlimited: 20,
  generations: 10,
  riddled_in: 10,
  earth_impossible: 10,
  earth_impossible_ish: 10,
};

// ---------------------------------------------------------------------------
// Formatters — convert raw score numbers to display strings
// ---------------------------------------------------------------------------

/**
 * Format a raw score value based on its format type.
 * @param {number} value
 * @param {'time'|'decimal'|'percent'|'integer'} format
 * @returns {string}
 */
export const formatScore = (value, format) => {
  if (value == null || !Number.isFinite(value)) return '—';

  switch (format) {
    case 'time': {
      const totalSeconds = Math.round(value / 1000);
      if (totalSeconds < 60) return `${totalSeconds}s`;
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      return `${mins}m ${secs}s`;
    }
    case 'decimal':
      return value.toFixed(2);
    case 'percent':
      return `${value}%`;
    case 'integer':
      return String(Math.round(value));
    default:
      return String(value);
  }
};
